import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import clsx from "clsx";
import { NumericFormat } from "react-number-format";
import { formatCurrency } from "../../../../Utils";
import { sumAllocations } from "../../../../Utils/DepositUtil";

/**
 * A customer's deposits, each with a tick and the amount to use from it.
 *
 * `allocations` is `{ [depositID]: amount }` and holds only ticked deposits.
 * Ticking one fills in what it has left, capped at what `limit` still allows,
 * so ticking never produces an amount the server would refuse. Shared by
 * "Pakai Uang Muka" on an invoice and the deposit box on a new invoice, which
 * follow the same rules.
 */
const DepositAllocationList = ({
  deposits = [],
  allocations = {},
  onChange,
  limit = 0,
  errors = {},
  disabled = false,
  idPrefix = "deposit-allocation",
}) => {
  const { t, i18n } = useTranslation();

  const toggle = (deposit, checked) => {
    const next = { ...allocations };
    if (!checked) {
      delete next[deposit.id];
    } else {
      const others = sumAllocations(next);
      const room = Math.max(0, Math.round(limit) - others);
      next[deposit.id] =
        Math.min(Math.round(deposit.remaining || 0), room) || 0;
    }
    onChange(next);
  };

  const setAmount = (deposit, value) =>
    onChange({ ...allocations, [deposit.id]: value ?? 0 });

  return (
    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
      {deposits.map((deposit) => {
        const checked = Object.prototype.hasOwnProperty.call(
          allocations,
          deposit.id,
        );
        const inputID = `${idPrefix}-${deposit.id}`;
        const error = errors[deposit.id];
        return (
          <li key={deposit.id} className="px-3 py-3">
            <div className="flex items-start gap-3">
              <input
                id={inputID}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => toggle(deposit, e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor={inputID} className="min-w-0 flex-1 text-sm">
                <span className="block font-medium text-gray-900">
                  {deposit.depositNumber}
                </span>
                <span className="block text-xs text-gray-500">
                  {dayjs(deposit.createdAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY")}{" "}
                  ·{" "}
                  <span className="tabular-nums">
                    {t("deposits.apply.remainingOf", {
                      interpolation: { escapeValue: false },
                      amount: formatCurrency(deposit.remaining),
                    })}
                  </span>
                </span>
              </label>
              {checked && (
                <div className="w-36 flex-shrink-0">
                  <NumericFormat
                    aria-label={`${t("deposits.fields.amount")} ${deposit.depositNumber}`}
                    value={allocations[deposit.id]}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={0}
                    allowNegative={false}
                    prefix="Rp"
                    disabled={disabled}
                    // react-number-format also reports a change of the `value`
                    // prop; only typing is the user's edit.
                    onValueChange={({ floatValue }, { source }) => {
                      if (source === "event") setAmount(deposit, floatValue);
                    }}
                    className={clsx(
                      "block w-full rounded-md border-0 py-1.5 text-right text-sm tabular-nums shadow-sm ring-1 focus:ring-2 focus:ring-inset disabled:bg-gray-50",
                      error
                        ? "ring-red-300 focus:ring-red-500"
                        : "ring-gray-300 focus:ring-blue-500",
                    )}
                  />
                </div>
              )}
            </div>
            {checked && error && (
              <p className="mt-1 text-right text-xs text-red-600">{t(error)}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default DepositAllocationList;
